// web/src/components/horse/NotesTimeline.jsx
// Per-horse activity log, rendered on the Horse Detail page.
// Staff see a compose box and pin/delete actions; everyone sees the timeline.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { IconButton, TrashIcon } from '../ui/icons';
import { useAuth } from '../../lib/auth';
import { fmtStamp } from '../../lib/dates';

const STAFF = ['owner', 'manager', 'trainer', 'groom'];

export default function NotesTimeline({ horseId }) {
  const qc = useQueryClient();
  const { activeRole } = useAuth();
  const isStaff = STAFF.includes(activeRole);

  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['notes', horseId],
    queryFn: () => api.get(`/notes?horseId=${horseId}`),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['notes', horseId] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const addNote = useMutation({
    mutationFn: (payload) => api.post('/notes', payload),
    onSuccess: () => {
      setBody('');
      setPinned(false);
      invalidate();
    },
  });

  const togglePin = useMutation({
    mutationFn: ({ id, next }) => api.patch(`/notes/${id}`, { pinned: next }),
    onSuccess: invalidate,
  });

  const removeNote = useMutation({
    mutationFn: (id) => api.del(`/notes/${id}`),
    onSuccess: invalidate,
  });

  const notes = data?.notes ?? [];
  const trimmed = body.trim();
  const canSubmit = trimmed.length > 0 && !addNote.isPending;

  function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    addNote.mutate({ horseId, body: trimmed, pinned });
  }

  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold mb-3">Notes</h2>

      {isStaff && (
        <form onSubmit={handleSubmit} className="card p-4 mb-4">
          <textarea
            className="input w-full min-h-[80px] resize-y"
            placeholder="Add a note about this horse…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={2000}
          />
          <div className="mt-2 flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-ink-600 select-none">
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
              />
              Pin to top
            </label>
            <button type="submit" className="btn-primary" disabled={!canSubmit}>
              {addNote.isPending ? 'Adding…' : 'Add Note'}
            </button>
          </div>
          {addNote.isError && (
            <p className="mt-2 text-sm text-red-600">Couldn’t save the note. Try again.</p>
          )}
        </form>
      )}

      {isLoading ? (
        <p className="text-ink-400 text-sm">Loading notes…</p>
      ) : isError ? (
        <p className="text-red-600 text-sm">Couldn’t load notes.</p>
      ) : notes.length === 0 ? (
        <p className="text-ink-400 text-sm">No notes yet.</p>
      ) : (
        <ul className="space-y-3">
          {notes.map((n) => (
            <li key={n.id} className="card p-4">
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="text-sm text-ink-500 min-w-0 truncate">
                  {n.pinned && <span className="text-amber-600 mr-1" aria-label="Pinned">★</span>}
                  <span className="font-medium text-ink-700">{n.authorName}</span>
                  <span className="text-ink-400"> · {formatWhen(n.createdAt)}</span>
                </div>
                {isStaff && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => togglePin.mutate({ id: n.id, next: !n.pinned })}
                      disabled={togglePin.isPending}
                      className="text-xs text-ink-500 hover:text-ink-800"
                    >
                      {n.pinned ? 'Unpin' : 'Pin'}
                    </button>
                    <IconButton
                      icon={TrashIcon}
                      tone="danger"
                      label="Delete note"
                      disabled={removeNote.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            'Delete this note? It goes to the archive and can be restored.',
                          )
                        )
                          removeNote.mutate(n.id);
                      }}
                    />
                  </div>
                )}
              </div>
              <p className="text-sm text-ink-800 whitespace-pre-wrap break-words">{n.body}</p>
              {n.photoUrls?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {n.photoUrls.map((url, i) => (
                    <img
                      key={i}
                      src={url}
                      alt=""
                      className="w-16 h-16 rounded-lg object-cover border border-ink-100"
                    />
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// Unix seconds → "just now / 3h ago / 2d ago / Mon D".
function formatWhen(unix) {
  if (!unix) return '';
  const then = unix * 1000;
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return fmtStamp(unix, 'short');
}
